import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { CaretDown, CaretUp } from '@phosphor-icons/react';

const BAND_COLORS = {
  green: 'bg-emerald-500 text-white',
  orange: 'bg-amber-500 text-white',
  red: 'bg-red-500 text-white',
};

export function CapacityCalendar({ refreshKey }) {
  const [days, setDays] = useState([]);
  const [currentCapacity, setCurrentCapacity] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/procurement/dashboard');
      setDays(data.calendar?.days || []);
      setCurrentCapacity(data.calendar?.current_capacity || data.current_capacity || 0);
    } catch (e) { /* silent — calendar is auxiliary */ }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!days.length) return null;

  return (
    <>
      <Card className="border-zinc-300 rounded-sm" data-testid="capacity-calendar">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-1">
            <button
              data-testid="capacity-calendar-toggle"
              onClick={() => setCollapsed(c => !c)}
              className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-800"
            >
              {collapsed ? <CaretDown size={14} /> : <CaretUp size={14} />}
              Daily Production Capacity (next 30 days)
              {currentCapacity > 0 && <span className="normal-case text-zinc-400">— {currentCapacity} windows/day</span>}
            </button>
            <div className="flex gap-2 text-[10px]">
              <span className="px-2 py-0.5 rounded bg-emerald-500 text-white">≤ 60%</span>
              <span className="px-2 py-0.5 rounded bg-amber-500 text-white">60–90%</span>
              <span className="px-2 py-0.5 rounded bg-red-500 text-white">&gt; 90%</span>
            </div>
          </div>
          {!collapsed && (
            <div className="grid grid-cols-7 gap-1 mt-3" data-testid="capacity-calendar-grid">
              {days.map((d) => (
                <button
                  key={d.date}
                  onClick={() => setSelectedDay(d)}
                  data-testid={`cap-day-${d.date}`}
                  className={`p-1.5 rounded-sm text-left transition-colors hover:ring-2 hover:ring-blue-300 ${
                    d.is_sunday ? 'bg-zinc-100 opacity-50' :
                    d.band === 'green' ? 'bg-emerald-50 border border-emerald-200' :
                    d.band === 'orange' ? 'bg-amber-50 border border-amber-200' :
                    'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm font-bold text-zinc-900">{d.date.slice(-2)}</span>
                    <span className="text-[9px] text-zinc-500">{d.day_of_week}</span>
                  </div>
                  <div className={`text-[10px] font-mono mt-0.5 rounded px-1 inline-block ${BAND_COLORS[d.band] || 'bg-zinc-200 text-zinc-600'}`}>
                    {Number(d.planned || 0).toFixed(0)}/{d.capacity}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>Production Load — {selectedDay?.date} ({selectedDay?.day_of_week})</DialogTitle>
          </DialogHeader>
          {selectedDay && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-sm border border-zinc-200 bg-zinc-50">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Capacity</div>
                  <div className="text-2xl font-bold">{selectedDay.capacity}</div>
                </div>
                <div className="p-3 rounded-sm border border-zinc-200 bg-zinc-50">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Planned</div>
                  <div className="text-2xl font-bold">{Number(selectedDay.planned || 0).toFixed(1)}</div>
                </div>
                <div className="p-3 rounded-sm border border-zinc-200 bg-zinc-50">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Utilization</div>
                  <div className="text-2xl font-bold">{selectedDay.utilization_pct}%</div>
                </div>
              </div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">JOs on this day</div>
              {(!selectedDay.jos || selectedDay.jos.length === 0) ? (
                <div className="text-sm text-zinc-400 text-center p-4">No JOs scheduled.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="p-2 text-left">JO Number</th>
                      <th className="p-2 text-left">Customer</th>
                      <th className="p-2 text-right">Windows</th>
                      <th className="p-2 text-left">Procurement</th>
                      <th className="p-2 text-left">Prod Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDay.jos.map((jo) => (
                      <tr key={jo.jo_number} className="border-b border-zinc-100">
                        <td className="p-2 font-mono text-xs font-bold text-blue-700">{jo.jo_number}</td>
                        <td className="p-2 text-xs">{jo.customer_name}</td>
                        <td className="p-2 text-right font-bold">{jo.windows}</td>
                        <td className="p-2"><Badge className="text-[10px] bg-zinc-200 text-zinc-700">{(jo.procurement_status || '').toUpperCase()}</Badge></td>
                        <td className="p-2"><Badge className="text-[10px] bg-blue-100 text-blue-800">{(jo.assigned_to_production_status || '').toUpperCase()}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
